import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CommentNode } from "./types";
import {
  formatCommentTime,
  getCommentDisplayExcerpt,
  getCommentDisplayText,
  getCommentScopeClass,
  getCommentScopeLabel,
} from "./commentText";
import { getCommentPath } from "./commentTreeModel";

interface CommentLanesViewProps {
  roots: CommentNode[];
  byId: Map<number, CommentNode>;
  selectedComment: CommentNode | null;
  currentEditId?: number | null;
  onSelect: (commentId: number) => void;
  onOpenReader: (commentId: number) => void;
  onOpenMap: (commentId: number) => void;
}

interface LaneDescriptor {
  title: string;
  subtitle: string;
  comments: CommentNode[];
  activeId: number | null;
  level: number;
}

interface LaneItemProps {
  comment: CommentNode;
  active: boolean;
  selected: boolean;
  currentEditId?: number | null;
  onSelect: (commentId: number) => void;
  onOpenReader: (commentId: number) => void;
  onOpenMap: (commentId: number) => void;
}

const LANE_WIDTH_STORAGE_KEY = "gistoscope.commentExplorer.laneWidths.v2";
const MIN_LANE_WIDTH = 180;
const DEFAULT_LANE_WIDTH = 320;

function loadStoredLaneWidths(): number[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(LANE_WIDTH_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  } catch {
    return [];
  }
}

function getDefaultLaneWidth(index: number): number {
  if (index === 0) return 320;
  if (index === 1) return 330;
  return DEFAULT_LANE_WIDTH;
}

function clampWidth(value: number, maxWidth: number): number {
  return Math.max(MIN_LANE_WIDTH, Math.min(maxWidth, value));
}

const LaneItem: React.FC<LaneItemProps> = ({
  comment,
  active,
  selected,
  currentEditId,
  onSelect,
  onOpenReader,
  onOpenMap,
}) => {
  const scopeClass = getCommentScopeClass(comment, currentEditId);

  return (
    <article
      className={`comment-explorer-lane-card ${scopeClass} ${active ? "is-active" : ""} ${selected ? "is-selected is-expanded" : ""} ${
        comment.isRemoved ? "is-removed" : ""
      }`}
    >
      <button type="button" className="comment-explorer-card-main" onClick={() => onSelect(comment.id)}>
        <div className="comment-explorer-card-kicker">
          <span className={`comment-explorer-scope-dot ${scopeClass}`} />
          <span>{getCommentScopeLabel(comment, currentEditId)}</span>
          <span>·</span>
          <span>{formatCommentTime(comment.createdAt)}</span>
        </div>

        <h3>{comment.user?.name || "Unknown user"}</h3>
        <p>{selected ? getCommentDisplayText(comment) : getCommentDisplayExcerpt(comment, 140)}</p>
      </button>

      <div className="comment-explorer-thread-meta">
        <span>{comment.directReplyCount} direct</span>
        <span>{comment.totalReplyCount} total</span>
        {comment.replies.length > 0 && <span>{comment.replies.length === 1 ? "one next lane" : `${comment.replies.length} next lane items`}</span>}
      </div>

      {selected && (
        <div className="comment-explorer-thread-actions">
          <button type="button" onClick={() => onOpenReader(comment.id)}>
            Read line
          </button>
          <button type="button" onClick={() => onOpenMap(comment.id)}>
            Map
          </button>
        </div>
      )}
    </article>
  );
};

const CommentLanesView: React.FC<CommentLanesViewProps> = ({
  roots,
  byId,
  selectedComment,
  currentEditId,
  onSelect,
  onOpenReader,
  onOpenMap,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const laneRefs = useRef<Array<HTMLElement | null>>([]);
  const dragRef = useRef<{ index: number; startX: number; startWidth: number } | null>(null);
  const [laneWidths, setLaneWidths] = useState<number[]>(loadStoredLaneWidths);
  const [resizingLaneIndex, setResizingLaneIndex] = useState<number | null>(null);
  const [focusedLaneIndex, setFocusedLaneIndex] = useState(0);

  const path = useMemo(() => (selectedComment ? getCommentPath(byId, selectedComment.id) : []), [byId, selectedComment]);

  const lanes = useMemo<LaneDescriptor[]>(() => {
    const nextLanes: LaneDescriptor[] = [
      {
        title: "Lane 0 · direct comments on this Gist",
        subtitle: "Choose a top-level comment to open its replies in the next lane.",
        comments: roots,
        activeId: path[0]?.id ?? null,
        level: 0,
      },
    ];

    path.forEach((comment, index) => {
      nextLanes.push({
        title: `Lane ${index + 1} · replies to selected comment`,
        subtitle: getCommentDisplayExcerpt(comment, 72),
        comments: comment.replies,
        activeId: path[index + 1]?.id ?? null,
        level: index + 1,
      });
    });

    return nextLanes;
  }, [path, roots]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(LANE_WIDTH_STORAGE_KEY, JSON.stringify(laneWidths));
    } catch {
      // Width persistence is a convenience only. Ignore storage failures.
    }
  }, [laneWidths]);

  const getVisibleLaneMaxWidth = useCallback(() => {
    const containerWidth = scrollRef.current?.clientWidth ?? (typeof window !== "undefined" ? window.innerWidth : 960);
    return Math.max(360, containerWidth - 32);
  }, []);

  const getLaneWidth = useCallback(
    (index: number) => clampWidth(laneWidths[index] ?? getDefaultLaneWidth(index), getVisibleLaneMaxWidth()),
    [getVisibleLaneMaxWidth, laneWidths]
  );

  const focusLane = useCallback((index: number) => {
    setFocusedLaneIndex(index);
    laneRefs.current[index]?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  }, []);

  const setLaneWidth = useCallback(
    (index: number, width: number) => {
      setLaneWidths((previous) => {
        const next = [...previous];
        next[index] = clampWidth(width, getVisibleLaneMaxWidth());
        return next;
      });
    },
    [getVisibleLaneMaxWidth]
  );

  const handleSelectFromLane = useCallback(
    (commentId: number, laneIndex: number) => {
      setFocusedLaneIndex(laneIndex);
      onSelect(commentId);
    },
    [onSelect]
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>, index: number) => {
    event.preventDefault();
    dragRef.current = {
      index,
      startX: event.clientX,
      startWidth: getLaneWidth(index),
    };
    setResizingLaneIndex(index);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleResizerDoubleClick = (index: number) => {
    setLaneWidth(index, getVisibleLaneMaxWidth());
    focusLane(index);
  };

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      if (!dragRef.current) return;
      const { index, startX, startWidth } = dragRef.current;
      setLaneWidth(index, startWidth + event.clientX - startX);
    };

    const handlePointerUp = () => {
      dragRef.current = null;
      setResizingLaneIndex(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [setLaneWidth]);

  const fitSelectedLane = () => {
    const selectedLaneIndex = Math.max(0, path.length - 1);
    setLaneWidth(selectedLaneIndex, getVisibleLaneMaxWidth());
    focusLane(selectedLaneIndex);
  };

  const compactLanes = () => {
    setLaneWidths((previous) => {
      const next = [...previous];
      lanes.forEach((_, index) => {
        next[index] = 220;
      });
      return next;
    });
  };

  const resetLaneWidths = () => {
    setLaneWidths([]);
  };

  return (
    <div className="comment-explorer-lanes-view" aria-label="Lane-by-lane comment navigation">
      <div className="comment-explorer-view-note comment-explorer-lanes-note">
        <span>
          Lanes show one level at a time. Drag vertical dividers to resize a lane up to the full visible width; other lanes move off-screen and remain reachable with horizontal scroll.
        </span>
        <div className="comment-explorer-lanes-tools" aria-label="Lane width controls">
          <button type="button" onClick={fitSelectedLane}>
            Fit selected lane
          </button>
          <button type="button" onClick={compactLanes}>
            Compact lanes
          </button>
          <button type="button" onClick={resetLaneWidths}>
            Reset widths
          </button>
        </div>
      </div>

      {path.length > 0 && (
        <div className="comment-explorer-lane-breadcrumbs" aria-label="Selected lane path">
          <button type="button" onClick={() => focusLane(0)}>
            Gist comments
          </button>
          {path.map((comment, index) => (
            <button
              key={comment.id}
              type="button"
              className={index === path.length - 1 ? "is-current" : ""}
              onClick={() => {
                onSelect(comment.id);
                focusLane(index);
              }}
            >
              Reply {index + 1} · {getCommentDisplayExcerpt(comment, 34)}
            </button>
          ))}
        </div>
      )}

      <div className={`comment-explorer-lanes-strip ${resizingLaneIndex !== null ? "is-resizing" : ""}`} ref={scrollRef}>
        <div className="comment-explorer-lanes-canvas">
          {lanes.map((lane, index) => (
            <React.Fragment key={`${lane.title}-${index}`}>
              <section
                ref={(element) => {
                  laneRefs.current[index] = element;
                }}
                className={`comment-explorer-lane-column ${focusedLaneIndex === index ? "is-focused" : ""}`}
                style={{ width: `${getLaneWidth(index)}px` }}
              >
                <div className="comment-explorer-lane-head">
                  <h3>{lane.title}</h3>
                  <span>{Math.round(getLaneWidth(index))}px</span>
                </div>
                <p className="comment-explorer-lane-subtitle">{lane.subtitle}</p>

                {lane.comments.length === 0 ? (
                  <div className="comment-explorer-reader-empty is-small">No comments in this lane yet.</div>
                ) : (
                  lane.comments.map((comment) => (
                    <LaneItem
                      key={comment.id}
                      comment={comment}
                      active={comment.id === lane.activeId}
                      selected={comment.id === selectedComment?.id}
                      currentEditId={currentEditId}
                      onSelect={(commentId) => handleSelectFromLane(commentId, index)}
                      onOpenReader={onOpenReader}
                      onOpenMap={onOpenMap}
                    />
                  ))
                )}
              </section>

              {index < lanes.length - 1 && (
                <div
                  className={`comment-explorer-lane-resizer ${resizingLaneIndex === index ? "is-dragging" : ""}`}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`Resize lane ${index}`}
                  title="Drag to resize this lane. Double-click to fit it to the visible width."
                  onPointerDown={(event) => handlePointerDown(event, index)}
                  onDoubleClick={() => handleResizerDoubleClick(index)}
                />
              )}
            </React.Fragment>
          ))}

          <div className="comment-explorer-lane-ghost" aria-hidden="true">
            More lanes can continue here. Use horizontal scroll when a selected lane is expanded to full width.
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentLanesView;
